import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { RESERVABLE_TYPES, MAX_DAYS_AWAY_TO_ALLOW_MOVE_IN } from 'shared/util/constants';

export function addOneMonth(date) {
  const dateObject = new Date(date);
  dateObject.setMonth(dateObject.getMonth() + 1);
  const twoDigitDay = `0${dateObject.getUTCDate()}`.slice(-2);
  const twoDigitMonth = `0${dateObject.getUTCMonth() + 1}`.slice(-2);
  const maxDate = `${dateObject.getUTCFullYear()}-${twoDigitMonth}-${twoDigitDay}`;
  return maxDate;
}

export const createLocalDate = date => {
  const utcFriendlyDate = date ? new Date(date) : new Date();
  utcFriendlyDate.setMinutes(utcFriendlyDate.getMinutes() + utcFriendlyDate.getTimezoneOffset());

  return utcFriendlyDate;
};

export function formatStartDateForIQS(date) {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const YYYY = date.getFullYear();
  const MM = month < 10 ? `0${month}` : month;
  const DD = day < 10 ? `0${day}` : day;
  return `${YYYY}-${MM}-${DD}`;
}

const DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN = 0;
const DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN_FOR_1_TO_10_CAPACITY = 7;
const DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN_FOR_GREATER_THAN_10_CAPACITY = 7;

export function getDefaultStartDate(daysFrom = DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN, countryISO) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return incrementBusinessDays(today, daysFrom, countryISO);
}

export function getFormattedReservableMoveInDate({
  dateOptions = { month: 'short', day: 'numeric' },
  locale,
  moveInDate
}) {
  return createLocalDate(moveInDate).toLocaleString(locale, dateOptions);
}

export function getReservableMoveInDate({ capacity, type, startDateFromIQS, startDateChosenByUser, countryISO }) {
  const isHotDesk = type === RESERVABLE_TYPES.hotDesk;

  return isHotDesk
    ? getValidHotDeskStartDate({ startDateChosenByUser, startDateFromIQS, countryISO })
    : getValidNonHotDeskStartDate({ capacity, startDateFromIQS, startDateChosenByUser, countryISO });
}

export function getValidNonHotDeskStartDate({ startDateChosenByUser, startDateFromIQS, capacity, countryISO }) {
  /* create Date() objects for manipulation and comparison */
  const selectedStartDate = createLocalDate(startDateChosenByUser);
  const iqsDate = createLocalDate(startDateFromIQS);

  /* find earliest start date that takes business rules into account */
  const earliestAllowedMoveInDate = getMinStartDateForNonHotDesk(capacity, countryISO);

  /*  earliest valid date is whichever date is later bewteen the adjusted IQS date and the earliest allowed date */
  const earliestValidMoveInDate = iqsDate < earliestAllowedMoveInDate ? earliestAllowedMoveInDate : iqsDate;

  /* if the selected date is after the earliest valid date, use the selected date */
  const selectedStartDateIsValid = selectedStartDate > earliestValidMoveInDate;

  if (selectedStartDateIsValid) {
    return selectedStartDate;
  }

  return formatStartDateForIQS(earliestValidMoveInDate);
}

export function getValidHotDeskStartDate({ startDateChosenByUser, startDateFromIQS }) {
  const selectedStartDate = createLocalDate(startDateChosenByUser);
  const iqsDate = createLocalDate(startDateFromIQS);
  const validStartDate = iqsDate < selectedStartDate ? selectedStartDate : iqsDate;
  return formatStartDateForIQS(validStartDate);
}

export function getMinStartDateForNonHotDesk(capacity = 1, countryISO) {
  if (capacity < 11) {
    return getDefaultStartDate(DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN_FOR_1_TO_10_CAPACITY, countryISO);
  }
  return getDefaultStartDate(DAYS_FROM_TODAY_TO_ALLOW_MOVE_IN_FOR_GREATER_THAN_10_CAPACITY, countryISO);
}

function incrementBusinessDays(date, dayOffset, countryISO) {
  for (let idx = 0; idx < dayOffset; idx++) {
    incrementBusinessDay(date, countryISO);
  }

  return date;
}

export function incrementBusinessDay(date) {
  date.setDate(date.getDate() + 1);

  return date;
}

export function isDateInThePastFromDefault(date, defaultDate = getDefaultStartDate()) {
  return date && createLocalDate(date) < defaultDate;
}

export function isDateTooFarIntoTheFuture(date) {
  const selectedDate = new Date(date);
  const comparisonDate = new Date();
  comparisonDate.setDate(comparisonDate.getDate() + MAX_DAYS_AWAY_TO_ALLOW_MOVE_IN);
  return selectedDate > comparisonDate;
}

export function getStartDateFilter() {
  const defaultStartDate = getDefaultStartDate();
  const startDateFilter = User.getValue(USER_STORAGE_KEY_MAP.startDateFilter);
  const hasStartDateFilter = !!startDateFilter;

  if (hasStartDateFilter) {
    const storedStartDate = createLocalDate(startDateFilter);
    const storedStartDateTooEarly = defaultStartDate > storedStartDate;

    if (!storedStartDateTooEarly) {
      return storedStartDate;
    }
  }

  return defaultStartDate;
}

export function formatIsoDate(date) {
  const year = date.getFullYear();
  const twoDigitMonth = date.toLocaleString('default', { month: '2-digit' });
  const twoDigitDay = date.toLocaleString('default', { day: '2-digit' });

  return `${year}-${twoDigitMonth}-${twoDigitDay}`;
}

export function formatDateForPicker(date) {
  const year = date.getFullYear();
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  return `${month} ${day}, ${year}`;
}

export function newDateFromIsoString(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const date = new Date();
  date.setFullYear(parseInt(year, 10));
  date.setMonth(parseInt(month, 10) - 1, parseInt(day, 10));

  return date;
}
